import React, {useEffect, useRef, useState} from 'react';
import AccountPicker from '../ui/transaction/AccountPicker';
import CommentField from '../ui/transaction/CommentField';
import useActiveAccountsSelector from '../../hooks/useActiveAccountsSelector';
import {IAccount} from '../../model/IAccount';
import {categoryApi} from '../../api/CategoriesService';

const BulkUploadForm = () => {
    const {activeAccounts} = useActiveAccountsSelector();
    const [account, setAccount] = useState<IAccount | null>(null);
    const [comment, setComment] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploadSession, {isLoading}] = categoryApi.useCreateBulkUploadSessionMutation();

    useEffect(() => {
        if (!account && activeAccounts.length > 0) {
            setAccount(activeAccounts[0]);
        }
    }, [activeAccounts, account]);

    const clearForm = () => {
        setComment('');
        setFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        if (activeAccounts.length > 0) {
            setAccount(activeAccounts[0]);
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selected = event.target.files?.[0] ?? null;
        if (selected && !selected.name.toLowerCase().endsWith('.csv')) {
            alert('Please choose a .csv file');
            event.target.value = '';
            setFile(null);
            return;
        }
        setFile(selected);
    };

    const handleUpload = async () => {
        if (!account) {
            alert('Please choose an account');
            return;
        }
        if (!file) {
            alert('Please choose a .csv file');
            return;
        }

        const result = await uploadSession({
            account_id: account.id,
            file,
            comment: comment.trim() || undefined,
        });

        if ('data' in result) {
            clearForm();
        } else {
            console.log(result.error);
            alert('Upload failed. Please try again.');
        }
    };

    return (
        <div className="mb-6 border-b border-gray-200 pb-4">
            <div className="flex flex-wrap items-end justify-center gap-4 mx-6 px-1">
                {activeAccounts.length > 0 && (
                    <AccountPicker
                        label="Account"
                        account={account}
                        setAccount={setAccount}
                        accounts={activeAccounts}
                    />
                )}
                <CommentField label="Comment" comment={comment} setComment={setComment}/>
                <div>
                    <label htmlFor="bulk_upload_file" className="ml-3 block text-sm font-medium text-gray-700">
                        CSV file
                    </label>
                    <div className="mt-1">
                        <input
                            ref={fileInputRef}
                            id="bulk_upload_file"
                            type="file"
                            accept=".csv,text/csv"
                            onChange={handleFileChange}
                            className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4
                                       file:rounded-md file:border-0 file:text-sm file:font-medium
                                       file:bg-blue-100 file:text-blue-800 hover:file:bg-blue-200"
                        />
                    </div>
                </div>
            </div>

            <div className="mx-10 my-4 flex items-center justify-center">
                <button
                    type="button"
                    className="mx-10 group relative flex justify-center py-2 px-6 border border-transparent
                               text-sm font-medium rounded-md bg-gray-200 text-red-500 hover:text-red-800"
                    onClick={clearForm}
                    disabled={isLoading}
                >
                    Clear
                </button>
                <button
                    type="button"
                    className="mx-10 group relative flex justify-center py-2 px-6 border border-transparent
                               text-sm font-medium rounded-md bg-green-100 text-green-800 hover:bg-green-200 focus:outline-none
                               disabled:opacity-50"
                    disabled={isLoading || !account || !file}
                    onClick={handleUpload}
                >
                    {isLoading ? 'Uploading…' : 'Upload'}
                </button>
            </div>
        </div>
    );
};

export default BulkUploadForm;
